import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { BitePanel, type BiteLink } from "./BitePanel";
import type { AllBite } from "./AllBitesTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  type AnyReportRow,
  type Classroom,
  type ClassroomReportRow,
  type IssueMap,
  type LessonReportRow,
  type ReportData,
  type ReportMeta,
  type ReportStats,
  formatEpochDate,
  reportDateLabel,
} from "./types";
import { GenerateDialog } from "./GenerateDialog";
import { LessonGenerateDialog } from "./LessonGenerateDialog";
import { ReportBody } from "./report_rendering/ReportBody";

function useResizable(initialWidth: number, minWidth: number, maxWidth: number) {
  const [width, setWidth] = useState(initialWidth);
  const dragging = useRef(false);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragging.current = true;
      const startX = e.clientX;
      const startWidth = width;

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragging.current) return;
        const delta = startX - ev.clientX;
        setWidth(Math.min(maxWidth, Math.max(minWidth, startWidth + delta)));
      };

      const onMouseUp = () => {
        dragging.current = false;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [width, minWidth, maxWidth],
  );

  return { width, onMouseDown };
}

export function ReportDetail({ onBack }: { onBack: () => void }) {
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get("reportId") ? Number(searchParams.get("reportId")) : null;

  const [report, setReport] = useState<AnyReportRow | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [renderMeta, setRenderMeta] = useState<ReportMeta | null>(null);
  const [issueMap, setIssueMap] = useState<IssueMap>({});
  const [allBites, setAllBites] = useState<AllBite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeLink, setActiveLink] = useState<BiteLink | null>(null);
  const [classroom, setClassroom] = useState<Classroom | null>(null);
  const [lessonName, setLessonName] = useState<string | null>(null);
  const [showRegen, setShowRegen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { width: panelWidth, onMouseDown: onResizeStart } = useResizable(360, 240, 1200);

  // Fetch report metadata from URL id
  useEffect(() => {
    if (!reportId) { setReport(null); return; }
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(`/api/internal/admin/reports/${reportId}`, { signal: ctrl.signal });
        if (r.ok) setReport(await r.json() as AnyReportRow);
        else setReport(null);
      } catch {
        setReport(null);
      }
    })();
    return () => ctrl.abort();
  }, [reportId]);

  // Fetch classroom or lesson name for the header
  useEffect(() => {
    if (!report) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        if (report.scope_type === "classroom") {
          const r = await fetch(`/api/internal/admin/classrooms/${report.classroom_id}`, { signal: ctrl.signal });
          if (r.ok) setClassroom(await r.json() as Classroom);
        } else {
          const r = await fetch(`/api/internal/admin/lessons/${report.lesson_id}/name`, { signal: ctrl.signal });
          if (r.ok) {
            const data = await r.json() as { name: string | null };
            if (data.name) setLessonName(data.name);
          }
        }
      } catch { /* ignore */ }
    })();
    return () => ctrl.abort();
  }, [report]);

  // Fetch rendered report content
  useEffect(() => {
    if (!reportId) return;
    const ctrl = new AbortController();
    setReportData(null);
    setLoading(true);
    setError(false);

    (async () => {
      try {
        const r = await fetch(`/api/internal/admin/classroom-reports/${reportId}/render`, { signal: ctrl.signal });
        if (!r.ok) throw new Error(`${r.status}`);
        const data = await r.json() as {
          report: ReportData;
          stats: ReportStats;
          renderMeta: ReportMeta;
          issueMap: IssueMap;
          allBites?: AllBite[];
        };
        setReportData(data.report);
        setStats(data.stats);
        setRenderMeta(data.renderMeta);
        setIssueMap(data.issueMap);
        setAllBites(data.allBites ?? []);
      } catch (e) {
        if ((e as { name?: string }).name !== "AbortError") setError(true);
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [reportId, refreshKey]);

  const dateLabel = report ? reportDateLabel(report) : "";
  const headerTitle = !report
    ? "Loading…"
    : report.scope_type === "lesson"
      ? `${lessonName ?? `Lesson ${report.lesson_id}`} — ${dateLabel}`
      : `${classroom?.name ?? `Classroom ${report.classroom_id}`} — ${dateLabel}`;

  const headerSubInfo = !report ? null
    : report.scope_type === "lesson" ? (
      <span className="text-xs text-muted-foreground">Lesson ID: {report.lesson_id}</span>
    ) : (
      <>
        <span className="text-xs text-muted-foreground">ID: {report.classroom_id}</span>
        {classroom?.teacher_name && (
          <span className="text-xs text-muted-foreground">· Teacher: {classroom.teacher_name}</span>
        )}
      </>
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%", height: "100%", minHeight: 0, overflowY: "auto" }}>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={onBack}>
          <ArrowLeft className="size-4" />
        </Button>
        <h2 className="text-sm font-semibold text-foreground">{headerTitle}</h2>
        {headerSubInfo}
        {report && (
          <>
            <span className="text-xs text-muted-foreground">{report.timezone}</span>
            <Badge
              variant={report.status === "generated" ? "default" : "secondary"}
              className="text-xs capitalize"
            >
              {report.status}
            </Badge>
            <Button size="sm" variant="outline" className="ml-2 text-xs h-7" onClick={() => setShowRegen(true)}>
              Regenerate
            </Button>
          </>
        )}
      </div>

      {showRegen && report?.scope_type === "classroom" && (
        <GenerateDialog
          classroomId={report.classroom_id}
          date={report.report_date}
          onClose={() => setShowRegen(false)}
          onDone={() => { setShowRegen(false); setRefreshKey((k) => k + 1); }}
        />
      )}

      {showRegen && report?.scope_type === "lesson" && (
        <LessonGenerateDialog
          lessonId={report.lesson_id}
          startDate={formatEpochDate(report.window_start)}
          endDate={formatEpochDate(report.window_end)}
          onClose={() => setShowRegen(false)}
          onDone={() => { setShowRegen(false); setRefreshKey((k) => k + 1); }}
        />
      )}

      {report?.status === "stale" && (
        <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 px-4 py-2 text-xs text-yellow-700 dark:text-yellow-400 shrink-0">
          Last regeneration failed — showing previous report. You can try regenerating again.
        </div>
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 320, overflow: "hidden" }}>
        <div style={{ flex: 1, overflowY: "auto", minWidth: 0 }}>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading report…
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">Failed to load report.</p>
          ) : reportData && stats && renderMeta && report ? (
            <div className="rounded-xl border bg-card p-6">
              <ReportBody
                report={reportData}
                stats={stats}
                meta={renderMeta}
                issueMap={issueMap}
                allBites={allBites}
                isLesson={report.scope_type === "lesson"}
                onOpenBite={setActiveLink}
              />
            </div>
          ) : null}
        </div>

        {activeLink && (
          <>
            <div
              onMouseDown={onResizeStart}
              style={{ width: 2, flexShrink: 0, cursor: "col-resize", background: "#bbb" }}
            />
            <BitePanel bite={activeLink} width={panelWidth} onClose={() => setActiveLink(null)} />
          </>
        )}
      </div>
    </div>
  );
}

// Re-export types needed by GenerateDialog callbacks in ReportDetail
export type { ClassroomReportRow, LessonReportRow };
